/**
 * Tiptap Editing Plugin
 *
 * Wraps the Tiptap rich-text editor as an EditingPlugin for the
 * CanvasEditor editing system. Handles markdown nodes.
 */

import type {
  EditingPlugin,
  EditingPluginContext,
  EditingSession,
  EditingResult,
} from "../editing";
import { createTiptapEditor, type TiptapInstance } from "./tiptap-editor";

/**
 * Create a Tiptap editing plugin for markdown nodes.
 *
 * @param getMarkdownContent - Extract markdown content from node data
 * @param onGrowNode - Called when content grows beyond node bounds
 */
export function createTiptapEditingPlugin(options?: {
  getMarkdownContent?: (
    nodeId: string,
    data: unknown,
  ) => string;
  onGrowNode?: (
    nodeId: string,
    editableElement: HTMLElement,
    nodeElement: HTMLElement,
  ) => void;
}): EditingPlugin {
  return {
    nodeTypes: ["markdown"],
    editableSelector: ".note-editor",
    startEditing(ctx: EditingPluginContext): EditingSession {
      const { element, nodeElement, input } = ctx;

      // Get markdown content from node data
      const markdownContent = options?.getMarkdownContent
        ? options.getMarkdownContent(ctx.nodeId, undefined)
        : ctx.originalContent;

      // Mark as editing
      element.classList.add("editing");

      // Push editing mode on input system
      input.pushMode("editing");

      let tiptapInstance: TiptapInstance | null = null;
      let stopped = false;

      tiptapInstance = createTiptapEditor(
        element,
        markdownContent,
        {
          onEscape: () => {
            ctx.requestStop(false);
          },
          onSave: () => {
            ctx.requestStop(true);
          },
          onUpdate: () => {
            options?.onGrowNode?.(ctx.nodeId, element, nodeElement);
          },
        },
      );

      function stopSession(save: boolean): EditingResult {
        if (stopped) return { content: "", size: undefined };
        stopped = true;

        let content = "";

        if (tiptapInstance) {
          content = tiptapInstance.getMarkdown();
          tiptapInstance.destroy();
          tiptapInstance = null;
        }

        // Pop editing mode
        input.popMode("editing");

        // Remove editing class
        element.classList.remove("editing");

        // Don't return size — the Tiptap editing DOM layout differs from the
        // static markdown render. The app should auto-fit after content update.
        return {
          content: save ? content : ctx.originalContent,
        };
      }

      return {
        getContent: () => tiptapInstance?.getMarkdown() ?? "",
        stop: (save: boolean) => stopSession(save),
        getToolbar: () => tiptapInstance?.getToolbar() ?? [],
        onToolbarChange: (callback: () => void) => {
          return tiptapInstance?.onToolbarChange(callback) ?? (() => {});
        },
      };
    },
  };
}
